/**
 * API fetching module for the Health Diary Monthly Report.
 *
 * Features:
 * - Fetches journal data from GET /customers/{customerId}/journals
 * - Converts all timestamps to UTC+8 (Malaysia time) immediately after fetch
 * - Caches the Current_Month report by request URL for 1 hour (Req 10)
 * - Caches past-month reports in a FIFO-bounded list keyed by
 *   `customerId + yearMonth` (max 50 entries) (Req 11)
 * - Tolerantly reuses values written by the legacy `monthly_report_cache_*`
 *   scheme, never throwing on read (Req 12.2)
 * - Preserves the existing UTC+8 conversion before any cache write (Req 12.3)
 */

import axios from 'axios';
import moment from 'moment';
import { API_DOMAIN, API_KEY } from '../../user_constant';
import { REPORT_CURRENT_MONTH_KEY, REPORT_LIST_KEY } from '../../cache/keys';
import { readJson, writeJson } from '../../cache/storage';
import type { FetchMonthlyJournalsParams, RawJournalResponse } from '../types';

// ─── Timezone Conversion (subtract 8 hours) ─────────────────────────────────

/**
 * Adjust a timestamp by subtracting 8 hours.
 * The API returns timestamps that are 8 hours ahead, so we subtract to correct.
 * Returns the adjusted ISO string. If input is falsy, returns it unchanged.
 */
function toLocalTime(utcTimestamp: string | null | undefined): string {
  if (!utcTimestamp) return utcTimestamp || '';
  const m = moment.utc(utcTimestamp);
  if (!m.isValid()) return utcTimestamp;
  return m.subtract(8, 'hours').format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';
}

/**
 * Recursively convert all `recorded_at` and `created_at` fields in an array of records
 * from UTC to UTC+8.
 */
function convertTimestampsInArray(records: any[]): any[] {
  if (!Array.isArray(records)) return [];
  return records.map((record) => {
    if (!record || typeof record !== 'object') return record;
    const converted = { ...record };
    if (converted.recorded_at) {
      converted.recorded_at = toLocalTime(converted.recorded_at);
    }
    if (converted.created_at) {
      converted.created_at = toLocalTime(converted.created_at);
    }
    // Also convert the `date` field if present (used by some API responses)
    if (converted.date && typeof converted.date === 'string') {
      const m = moment.utc(converted.date, 'YYYY-MM-DD HH:mm:ss');
      if (m.isValid()) {
        converted.date = m.subtract(8, 'hours').format('YYYY-MM-DD HH:mm:ss');
      }
    }
    return converted;
  });
}

/**
 * Convert all timestamp fields in the entire response to UTC+8.
 */
function convertResponseTimestamps(data: RawJournalResponse): RawJournalResponse {
  return {
    input: convertTimestampsInArray(data.input),
    output: convertTimestampsInArray(data.output),
    heartrate: convertTimestampsInArray(data.heartrate),
    bloodpressure: convertTimestampsInArray(data.bloodpressure),
    temperature: convertTimestampsInArray(data.temperature),
    spo2: convertTimestampsInArray(data.spo2),
    glucose: convertTimestampsInArray(data.glucose),
    adl: convertTimestampsInArray(data.adl),
    note: convertTimestampsInArray(data.note),
    weight: convertTimestampsInArray(data.weight),
  };
}

// ─── Report_Cache (current-month URL cache + past-month FIFO list) ───────────

/** Maximum number of entries retained in Report_List (Req 11.4). */
const REPORT_LIST_LIMIT = 50;
export const CACHE_SCHEMA_VERSION = 2;

/** Current-month cache freshness window: 1 hour (Req 10.2 / 10.3). */
const CURRENT_MONTH_TTL_MS = 60 * 60 * 1000;

/** Prefix used by the legacy per-month cache scheme (Req 12.2). */
const LEGACY_CACHE_PREFIX = 'monthly_report_cache_';

/**
 * A single past-month report entry held in Report_List.
 * `data` is already UTC+8-converted before being stored (Req 12.3).
 */
interface PastReportEntry {
  customerId: string;
  yearMonth: string;          // YYYY-MM
  data: RawJournalResponse;
  insertedAt: number;         // for FIFO ordering / debugging
  schemaVersion: number;
}

/** Stored shape of a Current_Month report entry, keyed by request URL. */
interface CurrentMonthCacheEntry {
  cachedAt: number;
  data: RawJournalResponse;
  schemaVersion: number;
}

/**
 * Build the `YYYY-MM` identifier for a requested month (Year_Month).
 */
function buildYearMonth(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Build the canonical request URL used as the Current_Month cache key.
 * Includes the customer id (already part of `baseUrl`) plus the date range so
 * that the same request always maps to the same key.
 */
function buildRequestUrl(baseUrl: string, startAt: string, endAt: string): string {
  return `${baseUrl}?start_at=${startAt}&end_at=${endAt}`;
}

/**
 * Determine whether the requested (year, month) is the Current_Month — the
 * calendar month containing Now.
 */
function isCurrentMonth(year: number, month: number): boolean {
  const now = moment();
  return now.year() === year && now.month() + 1 === month;
}

/**
 * Validate that a parsed value looks like a usable RawJournalResponse.
 * The minimal contract is that it has an `input` array (mirrors the legacy
 * validation), which is enough to treat it as reusable cached data.
 */
function isValidJournalResponse(value: unknown): value is RawJournalResponse {
  return (
    !!value &&
    typeof value === 'object' &&
    Array.isArray((value as RawJournalResponse).input) &&
    Array.isArray((value as RawJournalResponse).glucose)
  );
}

/**
 * Attempt to read a legacy `monthly_report_cache_{customerId}_{year}_{month}`
 * value. Reuse it when it parses to a valid RawJournalResponse, otherwise
 * ignore it. Never throws (Req 12.2).
 */
function readLegacyCache(
  customerId: string,
  year: number,
  month: number
): RawJournalResponse | null {
  const legacyKey = `${LEGACY_CACHE_PREFIX}${customerId}_${year}_${String(month).padStart(2, '0')}`;
  const parsed = readJson<RawJournalResponse>(legacyKey);
  return (
    isValidJournalResponse(parsed) &&
    (parsed as RawJournalResponse & { schemaVersion?: number }).schemaVersion === CACHE_SCHEMA_VERSION
  ) ? parsed : null;
}

/**
 * Read the FIFO-ordered Report_List from localStorage, tolerating any
 * malformed value by returning an empty list.
 */
function readReportList(): PastReportEntry[] {
  const list = readJson<PastReportEntry[]>(REPORT_LIST_KEY);
  return Array.isArray(list) ? list : [];
}

/**
 * Look up a past-month entry by customer id + Year_Month (Req 11.2).
 */
function findPastReport(
  customerId: string,
  yearMonth: string
): RawJournalResponse | null {
  const list = readReportList();
  const entry = list.find(
    (e) => e && e.customerId === customerId && e.yearMonth === yearMonth
  );
  return entry && entry.schemaVersion === CACHE_SCHEMA_VERSION && isValidJournalResponse(entry.data)
    ? entry.data
    : null;
}

/**
 * Append a past-month entry to Report_List, FIFO-evicting the earliest entry
 * when the list would exceed the limit (Req 11.3–11.5). Index 0 is the
 * earliest inserted and is evicted first.
 */
function appendPastReport(entry: PastReportEntry): void {
  const list = readReportList();
  list.push(entry);
  while (list.length > REPORT_LIST_LIMIT) {
    list.shift();
  }
  writeJson(REPORT_LIST_KEY, list);
}

// ─── Main Fetch Function ─────────────────────────────────────────────────────

/**
 * Fetches monthly journal data for a given customer and month.
 *
 * - Current_Month: serves the URL-keyed cache when its age is < 1 hour,
 *   otherwise fetches and rewrites the URL entry with a new timestamp.
 * - Past months: serves the matching Report_List entry when present, otherwise
 *   fetches, appends, and FIFO-evicts the earliest entry past the limit.
 * - Legacy `monthly_report_cache_*` values are read tolerantly and reused when
 *   valid, ignored otherwise, and never throw.
 * - All timestamps are converted to UTC+8 immediately after fetching, before
 *   any cache write.
 *
 * @param params - The customer ID, year, and month (1-12)
 * @returns The raw journal response with timestamps converted to UTC+8
 * @throws Error if the API returns an error response
 */
export async function fetchMonthlyJournals(
  params: FetchMonthlyJournalsParams
): Promise<RawJournalResponse> {
  const { customerId, year, month } = params;

  // Calculate first and last day of the month
  const startDate = moment({ year, month: month - 1, day: 1 });
  const endDate = startDate.clone().endOf('month');

  const startAt = startDate.format('YYYY-MM-DD');
  const endAt = endDate.format('YYYY-MM-DD');

  const baseUrl = `${API_DOMAIN}/careadmin/customers/${customerId}/journals`;
  const requestUrl = buildRequestUrl(baseUrl, startAt, endAt);
  const yearMonth = buildYearMonth(year, month);
  const current = isCurrentMonth(year, month);

  // ── Serve from cache where possible ────────────────────────────────────────
  if (current) {
    // Current_Month: URL-keyed entry, served only while age < 1 hour (Req 10.2).
    const entry = readJson<CurrentMonthCacheEntry>(REPORT_CURRENT_MONTH_KEY(requestUrl));
    if (
      entry &&
      entry.schemaVersion === CACHE_SCHEMA_VERSION &&
      typeof entry.cachedAt === 'number' &&
      isValidJournalResponse(entry.data) &&
      Date.now() - entry.cachedAt < CURRENT_MONTH_TTL_MS
    ) {
      return entry.data;
    }
  } else {
    // Past month: Report_List lookup first (Req 11.2), then legacy fallback.
    const pastHit = findPastReport(customerId, yearMonth);
    if (pastHit) {
      return pastHit;
    }
    const legacyHit = readLegacyCache(customerId, year, month);
    if (legacyHit) {
      return legacyHit;
    }
  }

  // ── Fetch fresh from the API ────────────────────────────────────────────────
  let result: RawJournalResponse;
  try {
    const response = await axios.get(baseUrl, {
      params: {
        start_at: startAt,
        end_at: endAt,
      },
      headers: {
        'x-api-key': API_KEY,
      },
    });

    const data = response.data;

    // Extract top-level arrays, treating missing arrays as empty
    const rawResult: RawJournalResponse = {
      input: Array.isArray(data.input) ? data.input : [],
      output: Array.isArray(data.output) ? data.output : [],
      heartrate: Array.isArray(data.heartrate) ? data.heartrate : [],
      bloodpressure: Array.isArray(data.bloodpressure) ? data.bloodpressure : [],
      temperature: Array.isArray(data.temperature) ? data.temperature : [],
      spo2: Array.isArray(data.spo2) ? data.spo2 : [],
      glucose: (Array.isArray(data.glucose) ? data.glucose : [])
        .filter((record: any) => readGlucoseValue(record) != null),
      adl: Array.isArray(data.adl) ? data.adl : [],
      note: Array.isArray(data.note) ? data.note : [],
      weight: Array.isArray(data.weight) ? data.weight : [],
    };

    // Convert all timestamps from UTC to UTC+8 (Malaysia time) BEFORE caching (Req 12.3)
    result = convertResponseTimestamps(rawResult);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const statusText = error.response?.statusText || 'Unknown error';
      throw new Error(
        `Failed to fetch monthly journal data: ${status} ${statusText}`
      );
    }
    throw new Error(
      `Failed to fetch monthly journal data: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }

  // ── Write to cache ──────────────────────────────────────────────────────────
  if (current) {
    // Rewrite the URL entry with a new timestamp (Req 10.3).
    const entry: CurrentMonthCacheEntry = {
      cachedAt: Date.now(),
      data: result,
      schemaVersion: CACHE_SCHEMA_VERSION,
    };
    writeJson(REPORT_CURRENT_MONTH_KEY(requestUrl), entry);
  } else {
    // Append to Report_List with FIFO eviction (Req 11.3–11.5).
    appendPastReport({
      customerId,
      yearMonth,
      data: result,
      insertedAt: Date.now(),
      schemaVersion: CACHE_SCHEMA_VERSION,
    });
  }

  return result;
}

export function readGlucoseValue(record: any): number | null {
  const raw = record?.glucose ?? record?.blood_glucose ?? record?.glucose_level ?? record?.value;
  if (raw === '' || raw == null) return null;
  const value = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(value) ? value : null;
}
